import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface SmsPurchase {
  id: string;
  quantity: number;
  amount: number;
  charge_amount: number;
  total_amount: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  payment_id: string;
  payment: {
    reference_code: string;
    payer_number: string;
    trx_id: string;
  };
}

export default function SmsHistory() {
  const { formatCurrency } = useCurrency();
  const [purchases, setPurchases] = useState<SmsPurchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('sms_purchases')
        .select(`
          *,
          payment:software_payments(reference_code, payer_number, trx_id)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPurchases(data || []);
    } catch (error) {
      console.error('Failed to load SMS purchase history:', error);
      alert('Failed to load SMS purchase history');
    } finally {
      setLoading(false);
    }
  };

  const approvePurchase = async (purchaseId: string, paymentId: string) => {
    try {
      const { error: smsError } = await supabase
        .from('sms_purchases')
        .update({ status: 'approved' })
        .eq('id', purchaseId);

      if (smsError) throw smsError;

      const { error: paymentError } = await supabase
        .from('software_payments')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
        })
        .eq('id', paymentId);

      if (paymentError) throw paymentError;

      alert('SMS purchase approved successfully!');
      loadPurchases();
    } catch (error) {
      console.error('Failed to approve SMS purchase:', error);
      alert('Failed to approve SMS purchase');
    }
  };

  const rejectPurchase = async (purchaseId: string, paymentId: string) => {
    try {
      const { error: smsError } = await supabase
        .from('sms_purchases')
        .update({ status: 'rejected' })
        .eq('id', purchaseId);

      if (smsError) throw smsError;

      const { error: paymentError } = await supabase
        .from('software_payments')
        .update({ status: 'rejected' })
        .eq('id', paymentId);

      if (paymentError) throw paymentError;

      alert('SMS purchase rejected');
      loadPurchases();
    } catch (error) {
      console.error('Failed to reject SMS purchase:', error);
      alert('Failed to reject SMS purchase');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="h-4 w-4 mr-1" />
            Rejected
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
            <Clock className="h-4 w-4 mr-1" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading SMS purchase history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">SMS Purchase History</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600 mb-1">Total Purchases</div>
          <div className="text-2xl font-bold text-gray-900">{purchases.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600 mb-1">Total SMS</div>
          <div className="text-2xl font-bold text-primary-600">
            {purchases.reduce((sum, p) => sum + p.quantity, 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600 mb-1">Approved</div>
          <div className="text-2xl font-bold text-green-600">
            {purchases.filter(p => p.status === 'approved').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600 mb-1">Pending</div>
          <div className="text-2xl font-bold text-amber-600">
            {purchases.filter(p => p.status === 'pending').length}
          </div>
        </div>
      </div>

      {/* Purchase History Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS Qty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  bKash Charge
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No SMS purchases found
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {purchase.payment?.reference_code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {purchase.quantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(purchase.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(purchase.charge_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(purchase.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(purchase.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(purchase.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {purchase.status === 'pending' ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => approvePurchase(purchase.id, purchase.payment_id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectPurchase(purchase.id, purchase.payment_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
